import { create } from 'zustand';

/**
 * POS Store — restaurant module only.
 *
 * posToken:    POS cashier JWT (in-memory only, never localStorage)
 * posStaff:    { _id, name, username, role }
 * activeShift: current open PosShift document
 * activeOrder: order being built — includes waiterId (ONLY place waiter identity lives)
 * billQueue:   PosOrder[] with status='billed'
 */
const usePosStore = create((set, get) => ({
  posToken: null,
  posStaff: null,
  activeShift: null,
  restaurantName: '',
  activeOrder: {
    orderId: null,      // set when adding items to an existing order
    type: 'dine-in',
    tableId: null,
    tableName: null,
    pax: 1,
    waiterId: null,
    waiterName: null,
    items: [],
  },
  billQueue: [],

  // ─── Auth ────────────────────────────────────────────────────────────────
  setPosSession: (token, staff, shift, restaurantName = '') =>
    set({ posToken: token, posStaff: staff, activeShift: shift, restaurantName }),

  clearPosSession: () =>
    set({
      posToken: null,
      posStaff: null,
      activeShift: null,
      restaurantName: '',
      activeOrder: { orderId: null, type: 'dine-in', tableId: null, tableName: null, pax: 1, waiterId: null, waiterName: null, items: [] },
      billQueue: [],
    }),

  setActiveShift: (shift) => set({ activeShift: shift }),

  // ─── Active Order ─────────────────────────────────────────────────────────
  setOrderType: (type) =>
    set((s) => ({ activeOrder: { ...s.activeOrder, type } })),

  setTable: (tableId, tableName, pax = 1) =>
    set((s) => ({ activeOrder: { ...s.activeOrder, orderId: null, tableId, tableName, pax: Math.max(1, pax) } })),

  setAddToOrder: (orderId, tableId, tableName, pax, waiterId, waiterName) =>
    set((s) => ({
      activeOrder: {
        ...s.activeOrder,
        orderId,
        tableId,
        tableName,
        pax: Math.max(1, pax || 1),
        waiterId,
        waiterName,
        items: [],
      },
    })),

  setWaiter: (waiterId, waiterName) =>
    set((s) => ({ activeOrder: { ...s.activeOrder, waiterId, waiterName } })),

  addItem: (item) =>
    set((s) => {
      const existing = s.activeOrder.items.find((i) => i.menuItemId === item.menuItemId);
      if (existing) {
        return {
          activeOrder: {
            ...s.activeOrder,
            items: s.activeOrder.items.map((i) =>
              i.menuItemId === item.menuItemId ? { ...i, quantity: i.quantity + 1 } : i
            ),
          },
        };
      }
      return { activeOrder: { ...s.activeOrder, items: [...s.activeOrder.items, { ...item, quantity: 1 }] } };
    }),

  updateItemQty: (menuItemId, quantity) =>
    set((s) => ({
      activeOrder: {
        ...s.activeOrder,
        items:
          quantity <= 0
            ? s.activeOrder.items.filter((i) => i.menuItemId !== menuItemId)
            : s.activeOrder.items.map((i) => (i.menuItemId === menuItemId ? { ...i, quantity } : i)),
      },
    })),

  removeItem: (menuItemId) =>
    set((s) => ({
      activeOrder: {
        ...s.activeOrder,
        items: s.activeOrder.items.filter((i) => i.menuItemId !== menuItemId),
      },
    })),

  clearOrder: () =>
    set((s) => ({
      activeOrder: {
        orderId: null,
        type: s.activeOrder.type,
        tableId: null,
        tableName: null,
        pax: 1,
        waiterId: null,
        waiterName: null,
        items: [],
      },
    })),

  // ─── Bill Queue ───────────────────────────────────────────────────────────
  setBillQueue: (orders) => set({ billQueue: orders }),

  addToBillQueue: (order) =>
    set((s) => ({ billQueue: [...s.billQueue.filter((o) => o._id !== order._id), order] })),

  removeFromBillQueue: (orderId) =>
    set((s) => ({ billQueue: s.billQueue.filter((o) => o._id !== orderId) })),
}));

export default usePosStore;
